import axios from "axios";
import type { z } from "zod";

import { attachAuthInterceptors } from "../auth/authFailure";
import { looseArray, looseParse } from "../looseParse";
import type { Product } from "./account";
import {
  accessKeySchema,
  authxApplicationSchema,
  authxAuthorizeResponseSchema,
  createAccessKeyResponseSchema,
  ipFiltersSchema,
} from "./authx.schema";

const instance = axios.create({ baseURL: window?.env?.AUTHX_API_URL });
const publicInstance = axios.create({ baseURL: window?.env?.AUTHX_API_URL });

attachAuthInterceptors(instance);

type AuthxAuthorizeSubjectType = "account" | "paccount";

export interface AuthxAuthorizeSubject {
  subject_type: AuthxAuthorizeSubjectType;
  subject_id: string;
  grants: string[];
}

export interface AuthxAuthorizeRequest {
  application_id: string;
  client_id: string;
  redirect_uri: string;
  code_challenge: string;
  code_challenge_method: string;
  state?: string;
  subjects: AuthxAuthorizeSubject[];
}

export type AuthxAuthorizeResponse = z.infer<
  typeof authxAuthorizeResponseSchema
>;

export type AuthxAuthorizationErrorCode =
  | "access_denied"
  | "invalid_request"
  | "server_error"
  | "temporarily_unavailable";

export interface AuthxErrorRedirectRequest {
  client_id: string;
  redirect_uri: string;
  error: AuthxAuthorizationErrorCode;
  error_description?: string;
  state?: string;
}

export const authorizeAuthxApplication = async (
  payload: AuthxAuthorizeRequest,
): Promise<AuthxAuthorizeResponse> => {
  const res = await instance.post<AuthxAuthorizeResponse>(
    "/oauth2/authorize",
    payload,
  );
  return looseParse(
    authxAuthorizeResponseSchema,
    res.data,
    "authorizeAuthxApplication",
  );
};

export const createAuthxErrorRedirect = async (
  applicationId: string,
  payload: AuthxErrorRedirectRequest,
): Promise<AuthxAuthorizeResponse> => {
  const res = await publicInstance.post<AuthxAuthorizeResponse>(
    `/applications/${encodeURIComponent(applicationId)}/error`,
    payload,
  );

  return looseParse(
    authxAuthorizeResponseSchema,
    res.data,
    "createAuthxErrorRedirect",
  );
};

export type AuthxApplication = z.infer<typeof authxApplicationSchema>;

export const getAuthxApplication = async (
  applicationId: string,
): Promise<AuthxApplication> => {
  const res = await instance.get<AuthxApplication>(
    `/applications/${encodeURIComponent(applicationId)}`,
  );

  return looseParse(authxApplicationSchema, res.data, "getAuthxApplication");
};

export type AccessKey = z.infer<typeof accessKeySchema>;
export type CreateAccessKeyResponse = z.infer<
  typeof createAccessKeyResponseSchema
>;

export interface GetAccessKeysParams {
  accountID?: string;
  product?: Product;
}

export const getAccessKeys = async ({
  accountID,
  product,
}: GetAccessKeysParams = {}): Promise<AccessKey[]> => {
  const res = await instance.get(
    product === "live"
      ? `/accounts/${accountID}/client-credentials`
      : `/paper-accounts/${accountID}/client-credentials`,
  );

  return looseParse(
    looseArray(accessKeySchema, "getAccessKeys"),
    res.data,
    "getAccessKeys",
  );
};

export interface CreateNewAccessKeyParams {
  accountID?: string;
  product?: Product;
}

export const createNewAccessKey = async ({
  accountID,
  product,
}: CreateNewAccessKeyParams = {}): Promise<CreateAccessKeyResponse> => {
  const res = await instance.post(
    accountID && product === "paper"
      ? `/paper-accounts/${accountID}/client-credentials`
      : `/accounts/${accountID}/client-credentials`,
    {
      type: "legacy",
      name: "Legacy key",
      grants: ["trading:all:read", "trading:all:write"],
    },
  );

  return looseParse(
    createAccessKeyResponseSchema,
    res.data,
    "createNewAccessKey",
  );
};

export interface DeleteAccessKeyParams {
  accessKeyID: string;
  accountID?: string;
  product?: Product;
}

export const deleteAccessKey = async ({
  accountID,
  accessKeyID,
  product,
}: DeleteAccessKeyParams): Promise<void> => {
  await instance.delete(
    accountID && product === "paper"
      ? `/paper-accounts/${accountID}/client-credentials/${accessKeyID}`
      : `/accounts/${accountID}/client-credentials/${accessKeyID}`,
  );
};

export type IPFilters = z.infer<typeof ipFiltersSchema>;

export const getIPFilters = async (accountID: string): Promise<IPFilters> => {
  const res = await instance.get<IPFilters>(
    `/accounts/${accountID}/ip-filters/api`,
  );

  return looseParse(ipFiltersSchema, res.data, "getIPFilters");
};
